import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface EmpresaResponsavel {
  id: number;
  nome: string;
  cnpj?: string;
  telefone?: string;
  email?: string;
  ativo: boolean;
  created_at: string;
  updated_at: string;
}

export function useEmpresasResponsaveis() {
  return useQuery({
    queryKey: ['empresas-responsaveis'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('empresas_responsaveis')
        .select('*')
        .eq('ativo', true)
        .order('nome', { ascending: true });

      if (error) {
        console.error('Erro ao buscar empresas:', error);
        throw error;
      }

      return data as EmpresaResponsavel[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos
  });
}

export function useCreateEmpresaResponsavel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (empresa: {
      nome: string;
      cnpj?: string;
      telefone?: string;
      email?: string;
      ativo?: boolean;
    }) => {
      const { data, error } = await supabase
        .from('empresas_responsaveis')
        .insert(empresa)
        .select()
        .single();

      if (error) {
        console.error('Erro ao criar empresa:', error);
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['empresas-responsaveis'] });
    }
  });
}

export function useEditarEmpresaResponsavel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...empresa }: {
      id: number;
      nome: string;
      cnpj?: string;
      telefone?: string;
      email?: string;
      ativo?: boolean;
    }) => {
      const { data, error } = await supabase
        .from('empresas_responsaveis')
        .update({
          ...empresa,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Erro ao editar empresa:', error);
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['empresas-responsaveis'] });
    }
  });
}

export function useExcluirEmpresaResponsavel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const { error } = await supabase
        .from('empresas_responsaveis')
        .update({ ativo: false })
        .eq('id', id);

      if (error) {
        console.error('Erro ao excluir empresa:', error);
        throw error;
      }

      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['empresas-responsaveis'] });
    }
  });
}